/**
 * Non-CV ("portable") skills are one page, not a wizard: icon, name, a
 * description and the recommended model. On create
 * ("Build a skill", Write with AI) the AI drafts the method from the
 * description; on edit the method is shown as an Instructions field too.
 *
 * {@link PortableSkillFields} is the field group, shared by the Build a skill
 * sheet and {@link PortableSkillEditor}, the edit screen hosted by
 * app/(app)/skills/create.tsx when the loaded skill is not a CV skill.
 */
import { localeRowDirection } from '@/lib/rtl-layout';
import { useState } from 'react';
import {StyleSheet, View} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { Text } from '@/components/common/AppText';
import { NativeButton } from '@/components/common/NativeButton';
import { useTheme } from '../../hooks/useTheme';
import { useFont } from '../../providers/FontProvider';
import { useLocale } from '../../hooks/useLocale';
import { useHaptics } from '../../hooks/useHaptics';
import { useToast } from '../../hooks/useToast';
import { useUpgradeFromFormSheet } from '../../hooks/useUpgradeFromFormSheet';
import { useSheetTopInset } from '../../hooks/useSheetTopInset';
import { pickImageFromLibrary, uploadImageAsset } from '../../lib/image-upload';
import { isEntitlementDenied, updateSkill } from '../../lib/skills-api';
import { entitlementMode } from '../../lib/skills-entitlement';
import { INSTRUCTIONS_MAX, draftToSkillInput, type SkillBuilderDraft } from './skill-builder-shared';
import { Field, IconPickerButton, WizardInput } from './skill-builder-inputs';
import { ModelPicker } from './skill-builder-fields';
import { SkillBuilderScrollView } from './SkillBuilderScrollView';

export type PatchDraft = (partial: Partial<SkillBuilderDraft>) => void;

/**
 * The skill's icon: preset glyph grid plus an upload from the photo library.
 * Extracted so the "Write with AI" describe step can offer the same control
 * before there is a draft to review.
 */
export function SkillIconField({
  draft,
  patch,
}: {
  draft: SkillBuilderDraft;
  patch: PatchDraft;
}) {
  const [uploading, setUploading] = useState(false);
  return (
    <IconPickerButton
      value={draft.iconKey}
      region={draft.region}
      customUri={draft.iconUrl || null}
      uploading={uploading}
      onChange={(icon) => patch({ iconKey: icon, iconUrl: '' })}
      onPickImage={async () => {
        const asset = await pickImageFromLibrary();
        if (!asset) return;
        setUploading(true);
        try {
          const url = await uploadImageAsset(asset, {
            kind: 'skill-icon',
            fileName: asset.name,
            mimeType: asset.type,
          });
          patch({ iconUrl: url });
        } catch {
          // Upload failed: keep the current icon.
        } finally {
          setUploading(false);
        }
      }}
    />
  );
}

export function PortableSkillFields({
  draft,
  patch,
  showInstructions = false,
  disabled = false,
}: {
  draft: SkillBuilderDraft;
  patch: PatchDraft;
  /** Edit shows the method; create leaves it to the AI. */
  showInstructions?: boolean;
  /**
   * Review-after-generate folds the Arabic pair away (it arrives prefilled and
   * most authors will not touch it). The edit screen keeps it open.
   */
  /** While the AI is drafting, the fields are read-only. */
  disabled?: boolean;
}) {
  const { t } = useTranslation('common');
  const { isRTL } = useLocale();
  const rowDir = localeRowDirection(isRTL);

  return (
    <View style={[styles.col16, disabled ? styles.dimmed : null]}>
      {/* Icon + name on one line, the same height. */}
      <Field label={t('skills.builder.basics.nameLabel')}>
        <View style={{ flexDirection: rowDir, alignItems: 'center', gap: 12 }}>
          <SkillIconField draft={draft} patch={patch} />
          <View style={{ flex: 1, minWidth: 0 }}>
            <WizardInput
              value={draft.name}
              onChangeText={(name) => patch({ name })}
              placeholder={t('skills.builder.basics.namePlaceholder')}
            />
          </View>
        </View>
      </Field>

      <Field label={t('skills.builder.basics.descriptionLabel')}>
        <WizardInput
          value={draft.description}
          onChangeText={(description) => patch({ description })}
          placeholder={t('skills.builder.basics.descriptionPlaceholder')}
          multiline
          minHeight={96}
        />
      </Field>

      {showInstructions ? (
        <Field label={t('skills.builder.type.instructionsLabel')}>
          <WizardInput
            value={draft.instructions}
            onChangeText={(instructions) => patch({ instructions })}
            placeholder={t('skills.builder.type.instructionsPlaceholder')}
            multiline
            minHeight={160}
            maxLength={INSTRUCTIONS_MAX}
          />
        </Field>
      ) : null}
    </View>
  );
}

/** Edit screen for a non-CV skill: every field on one page, then Save. */
export function PortableSkillEditor({
  skillId,
  initialDraft,
  onDone,
}: {
  skillId: string;
  initialDraft: SkillBuilderDraft;
  onDone: () => void;
}) {
  const { t } = useTranslation('common');
  const { theme } = useTheme();
  const { fonts } = useFont();
  const { isRTL } = useLocale();
  const insets = useSafeAreaInsets();
  const haptics = useHaptics();
  const toast = useToast();
  const openUpgrade = useUpgradeFromFormSheet();
  const topInset = useSheetTopInset();
  const rowDir = localeRowDirection(isRTL);

  const [draft, setDraft] = useState<SkillBuilderDraft>(initialDraft);
  const [submitting, setSubmitting] = useState(false);
  const patch: PatchDraft = (partial) => setDraft((prev) => ({ ...prev, ...partial }));
  const canSave = draft.name.trim().length > 0 && draft.description.trim().length > 0 && !submitting;

  const save = async () => {
    if (!canSave) return;
    haptics.trigger('light');
    setSubmitting(true);
    try {
      await updateSkill(skillId, draftToSkillInput(draft));
      haptics.trigger('success');
      toast.success(t('skills.builder.saved'));
      onDone();
    } catch (error) {
      if (isEntitlementDenied(error)) {
        openUpgrade(entitlementMode(error.decision));
        return;
      }
      toast.error(t('skills.builder.failed'), error instanceof Error ? error.message : undefined);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <View style={[styles.screen, { backgroundColor: theme.background }]}>
      <SkillBuilderScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.content, { paddingTop: topInset }]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        bottomOffset={96}
      >
        <PortableSkillFields draft={draft} patch={patch} showInstructions />
        <View>
          <Text style={[styles.groupTitle, { color: theme.text, fontFamily: fonts.medium, marginBottom: 10 }]}>
            {t('skills.newFlow.basics.recommendedModel')}
          </Text>
          <ModelPicker
            value={draft.model}
            onChange={(model) => patch({ model })}
            onUpgrade={() => openUpgrade('individual')}
          />
        </View>
      </SkillBuilderScrollView>
      <View
        style={[
          styles.footer,
          {
            flexDirection: rowDir,
            borderTopColor: theme.border,
            paddingBottom: Math.max(insets.bottom, 16),
            backgroundColor: theme.background,
          },
        ]}
      >
        <NativeButton
          title={t('actions.cancel')}
          variant="outline"
          size="lg"
          pill
          disabled={submitting}
          onPress={onDone}
          style={styles.footerBtn}
        />
        <NativeButton
          title={t('skills.builder.save')}
          color="contrast"
          size="lg"
          pill
          disabled={!canSave}
          loading={submitting}
          onPress={() => void save()}
          style={styles.footerBtn}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  scroll: { flex: 1 },
  content: { paddingHorizontal: 20, paddingBottom: 32, gap: 20 },
  col16: { gap: 16 },
  dimmed: { opacity: 0.55, pointerEvents: 'none' },
  groupTitle: { fontSize: 15 },
  footer: {
    paddingHorizontal: 20,
    paddingTop: 12,
    gap: 10,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  footerBtn: { flex: 1 },
});
